import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AdminDashboard from "./admindashboard";
import ProcessAppointmentPopup from "./processAppointmentPopup";
import RefundAmount from "./refundAmount";
import { Pagination } from "react-bootstrap";
import "./hm-theme.css";

function PaymentGateway() {
  const [loading, setLoading] = useState(false);
  const [userPayments, setUserPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [datesInitialized, setDatesInitialized] = useState(false);

  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("data"))?.token;

  // Format date to YYYY-MM-DD
  const formatDateToYMD = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  // Set default dates on mount
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const today = new Date();
    setFromDate(formatDateToYMD(firstDay));
    setToDate(formatDateToYMD(today));
    setDatesInitialized(true);
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (datesInitialized) {
      setCurrentPage(0);
    }
  }, [fromDate, toDate, searchTerm, datesInitialized]);

  // Fetch data when page changes or dates are initialized
  useEffect(() => {
    if (datesInitialized && fromDate && toDate) {
      getData(currentPage);
    }
  }, [currentPage, fromDate, toDate, datesInitialized]);

  const getData = async (pageNumber = 0) => {
    // Don't make API call if dates are not set
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true);
      const payload = { fromDate, toDate };
      const response = await axios.post(
        "/api/payment/getAllPaymentsWithStatus",
        payload,
        {
          params: { page: pageNumber },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUserPayments(response.data.content || []);
      setCurrentPage(response.data.number);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isProcessAppointment, setIsProcessAppointment] = useState(false);
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);

  const handleProcessAppointment = (payment) => {
    setSelectedPayment(payment);
    setIsProcessAppointment(true);
  };

  const handleCancelProcessAppointment = () => {
    setIsProcessAppointment(false);
    getData(currentPage);
  };

  const handleCancelClick = (payment) => {
    setSelectedPayment(payment);
    setIsCancelPopupOpen(true);
  };

  const handleCloseCancelPopup = () => {
    setSelectedPayment(null);
    setIsCancelPopupOpen(false);
    getData(currentPage);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxToShow = 5;
    let start = Math.max(0, currentPage - 2);
    let end = Math.min(totalPages - 1, start + maxToShow - 1);

    // Adjust start if we're near the end
    if (currentPage >= totalPages - 3) {
      start = Math.max(0, totalPages - maxToShow);
    }

    for (let i = start; i <= end; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const handleError = (error) => {
    if (error.response?.status === 401) {
      toast.error("Your session has expired. Please login.");
      navigate("/signin");
    } else {
      toast.error(error.response?.data?.message || error.response?.data?.code || error.message || "An error occurred.");
    }
  };

  const getStatusClass = (status) => {
    if (!status) return "hm-badge hm-badge-info";
    const s = status.toUpperCase();
    if (s === "CAPTURED") return "hm-badge hm-badge-success";
    if (s.includes("FAIL") || s.includes("REFUND")) return "hm-badge hm-badge-danger";
    if (s.includes("PENDING")) return "hm-badge hm-badge-warning";
    return "hm-badge hm-badge-info";
  };

  return (
    <div>
      <AdminDashboard />
      {loading && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">payment</i>
                Payment Gateway
              </h2>
              <p className="hm-page-subtitle">View and manage all payment transactions</p>
            </div>
          </div>

          {/* Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_list</i>
                Filter by Date Range
              </h3>
            </div>
            <div className="hm-card-body">
              <div className="d-flex flex-wrap gap-3 align-items-end">
                <div>
                  <label className="form-label small fw-semibold mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm rounded-3"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label small fw-semibold mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm rounded-3"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <button
                  className="btn-hm-primary"
                  onClick={() => getData(0)}
                  type="button"
                >
                  <i className="material-icons" style={{ fontSize: "1rem" }}>search</i>
                  Apply Filter
                </button>
              </div>
            </div>
          </div>

          {/* Payments Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">receipt_long</i>
                Payment Transactions
              </h3>
              <span className="hm-badge hm-badge-primary">{userPayments.length} records</span>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Company</th>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Order ID</th>
                      <th>Payment ID</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPayments.length === 0 ? (
                      <tr>
                        <td colSpan="8">
                          <div className="hm-empty-state">
                            <i className="material-icons">receipt</i>
                            <p>{loading ? "Loading..." : "No payment records found"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      userPayments.map((payment, index) => (
                        <tr key={payment.paymentId || index}>
                          <td>{currentPage * 10 + index + 1}</td>
                          <td>{payment.company?.companyname || '—'}</td>
                          <td>
                            <div className="fw-semibold">{payment.employee?.name} {payment.employee?.lastname}</div>
                            {payment.memberName && (
                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>{payment.memberName}</div>
                            )}
                          </td>
                          <td>{payment.date || '—'}</td>
                          <td><code style={{ fontSize: "0.78rem" }}>{payment.orderId || '—'}</code></td>
                          <td><code style={{ fontSize: "0.78rem" }}>{payment.paymentId || '—'}</code></td>
                          <td className="fw-bold">₹ {payment.amount || 0}</td>
                          <td>
                            <span className={getStatusClass(payment.paymentStatus)}>
                              {payment.paymentStatus}
                            </span>
                            {payment.paymentStatus === "CAPTURED" && payment.appointmentStatus !== "APPOINTMENT COMPLETED" && (
                              <div className="d-flex gap-2 mt-2">
                                <button
                                  className="btn btn-success btn-sm rounded-pill px-3"
                                  onClick={() => handleProcessAppointment(payment)}
                                >
                                  Validate
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                  onClick={() => handleCancelClick(payment)}
                                >
                                  Refund
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center py-3">
                  <Pagination className="hm-pagination">
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                    {renderPageNumbers()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                  </Pagination>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {isProcessAppointment && (
        <ProcessAppointmentPopup
          isOpen={isProcessAppointment}
          onRequestClose={handleCancelProcessAppointment}
          payment={selectedPayment}
        />
      )}

      {isCancelPopupOpen && (
        <RefundAmount
          isOpen={isCancelPopupOpen}
          onRequestClose={handleCloseCancelPopup}
          payment={selectedPayment}
        />
      )}
    </div>
  );
}

export default PaymentGateway;